import {
  CONTACT_EMAIL,
  CUTOUT_UNIT_PRICE_KRW,
  // ⚠️ 제2조 2 의 문장. `W-26` · `A-26` · `W-26p` 결제 버튼 앞 고지와 한 상수다(`money-copy.ts`).
  // 이 화면에 글자를 다시 적지 않는다. 네 자리가 한 문장을 쓴다(PRD `7.5.9`).
  MADE_SHEET_NO_REFUND_NOTICE,
  MAX_IMAGES_PER_JOB,
  REFUND_POLICY_EFFECTIVE_DATE,
  REFUND_RETURN_TIMING,
  SERVICE_PRODUCT_NAME,
  SOURCE_RETENTION_DAYS,
  VAT_INCLUDED,
  formatKrw,
} from "@makit/shared";

import { PublicLayout } from "./PublicLayout";

/** `P-02` 환불정책. 결제사 심사의 「환불정책 확인 URL」.
 *
 * **글자의 정본은 PRD `7.0` 「로그인 전 화면의 확정 문구」 (1)이다**(2026-10-04 `design-manager` 확정).
 * 조문 꼴이고, 조 제목에 `환불` 을 쓰지 않는다. 그 낱말은 **문서 이름에만** 쓴다(PRD `7.5.9`).
 * 화면에서 돈의 상태는 `돌아갑니다` 계열이고, 우리가 하는 일은 능동형 `취소합니다` 다.
 *
 * ⭐ **반환은 수동 취소다(2026-10-04 사용자 결정, B안).** 금액을 돌려보내는 코드는 아직 0건이고,
 * 이 문서의 약속을 지키는 것은 사람의 절차(`docs/REFUND_RUNBOOK.md`)다. 그래서 시작점 둘(셀러가 알려
 * 준 때 / 원본 사진 보관이 끝난 뒤)만 말하고 **우리 쪽 처리 기한이나 자동성을 약속하지 않는다**
 * (`6A.12` · `L-F50` · `L-F56`). 제3조의 「돌아갑니다」는 모두 「제4조에 따라」로 제4조를 가리킨다.
 * `scripts/public-pages.test.mjs` 가 이 파일에서 금지 표현과 그 가리킴을 확인한다.
 *
 * ⚠️ 제2조 2 는 결제 버튼 앞의 고지(`A-26` · `W-26` · `W-26p`)와 **글자 하나 다르지 않게**
 * 쓴다(PRD `7.5.9`). 그래서 글자를 여기 적지 않고 `MADE_SHEET_NO_REFUND_NOTICE` 를 읽는다.
 *
 * 숫자와 값은 상수에서 읽는다. 단가 `CUTOUT_UNIT_PRICE_KRW`, 한 건 장수 `MAX_IMAGES_PER_JOB`,
 * 원본 보관 `SOURCE_RETENTION_DAYS`, 시행일 `REFUND_POLICY_EFFECTIVE_DATE`, 제4조 `REFUND_RETURN_TIMING`,
 * 연락처 `CONTACT_EMAIL`(사업자 정보의 이메일과 한 상수). */
export function RefundPolicyScreen() {
  const unitPrice = formatKrw(CUTOUT_UNIT_PRICE_KRW);

  return (
    <PublicLayout title="환불정책 · makit">
      <article className="policy">
        <h1>환불정책</h1>
        <p className="meta">
          {SERVICE_PRODUCT_NAME} · 시행일 <span className="mono">{REFUND_POLICY_EFFECTIVE_DATE}</span>
        </p>

        <h2>제1조 이 정책이 다루는 것</h2>
        <p className="prose">
          이 정책은 {SERVICE_PRODUCT_NAME}(사진 1장당 {unitPrice}, {VAT_INCLUDED})를 결제한 뒤의 취소와 돌아가는
          금액을 다룹니다. 결제는 작업 한 건마다 하고, 한 건에 사진을 최대 {MAX_IMAGES_PER_JOB}장까지 담습니다.
        </p>

        <h2>제2조 결제 취소</h2>
        <ol>
          <li className="prose">
            결제한 뒤 만들기가 시작되기 전에 제5조의 연락처로 알려 주시면 전액 취소합니다. 알려 주신 때를 기준으로
            봅니다.
          </li>
          <li className="prose">{MADE_SHEET_NO_REFUND_NOTICE}</li>
          <li className="prose">만든 장을 다시 만들면 새로 만드는 일이라 1장당 {unitPrice}이 듭니다.</li>
          <li className="prose">
            안내한 내용과 다르게 만들어진 장은 제5조의 연락처로 알려 주세요. 확인한 뒤 그 장을 다시 만들어
            드리거나, 그 장 금액을 결제하신 수단으로 취소합니다. 이 경우 다시 만들어도 더 내실 금액이 없습니다.
          </li>
        </ol>

        <h2>제3조 못 만든 장의 금액</h2>
        <ol>
          <li className="prose">
            못 만든 장은 못 만든 까닭과 상관없이 같게 다룹니다. 다시 만들어도 더 내실 금액이 없고, 다시 만들지
            않으시면 그 장 금액이 제4조에 따라 돌아갑니다.
          </li>
          <li className="prose">만들기를 시작하지 못하고 대기로 남은 장도 1과 같게 다룹니다.</li>
          <li className="prose">
            원본 사진은 올린 날부터 {SOURCE_RETENTION_DAYS}일 보관합니다. 그 뒤에는 못 만든 장도 다시 만들 수
            없고, 그 장 금액이 제4조에 따라 돌아갑니다.
          </li>
        </ol>

        <h2>제4조 금액이 돌아가는 때</h2>
        <p className="prose">{REFUND_RETURN_TIMING}</p>

        <h2>제5조 문의</h2>
        <p className="prose">
          결제 취소와 돌아가는 금액에 대한 문의는 <span className="mono">{CONTACT_EMAIL}</span>로 받습니다.
        </p>
      </article>
    </PublicLayout>
  );
}
